import type { ActionFunctionArgs } from "react-router-dom";
import { redirect } from "react-router-dom";
import { podcastAPI, APIError } from "../../lib/api";
import { createPodcastSchema, updatePodcastSchema } from "../../schemas";
import { extractFieldErrors, parseFormData } from "../../utils";
import type { ActionResult } from "./auth.actions";

export async function createPodcastAction({
  request,
  params,
}: ActionFunctionArgs): Promise<ActionResult> {
  const { id: orgId } = params;

  if (!orgId) {
    return { success: false, error: "Organization ID is required" };
  }

  const formData = await parseFormData(request);

  // Handle checkbox fields that may not be present
  const dataWithDefaults = {
    ...formData,
    explicit: formData.explicit === "true" || formData.explicit === "on",
    isPremium: formData.isPremium === "true" || formData.isPremium === "on",
  };

  const result = createPodcastSchema.safeParse(dataWithDefaults);

  if (!result.success) {
    return { success: false, fieldErrors: extractFieldErrors(result.error) };
  }

  try {
    const podcast = await podcastAPI.create(orgId, {
      name: result.data.name,
      slug: result.data.slug || undefined,
      description: result.data.description || undefined,
      coverArtworkUrl: result.data.coverArtworkUrl || undefined,
      language: result.data.language,
      category: result.data.category || undefined,
      explicit: result.data.explicit,
      timezone: result.data.timezone,
      isPremium: result.data.isPremium,
      revocationPolicy: result.data.revocationPolicy,
      revocationGraceDays: result.data.revocationGraceDays,
    });

    if (!podcast?.id) {
      return { success: false, error: "Failed to create podcast — missing ID in response" };
    }

    return {
      success: true,
      data: { id: podcast.id, redirectTo: `/organizations/${orgId}/podcasts/${podcast.id}` },
    };
  } catch (error) {
    if (error instanceof APIError) {
      return { success: false, error: error.message };
    }
    return { success: false, error: "An unexpected error occurred" };
  }
}

export async function updatePodcastAction({
  request,
  params,
}: ActionFunctionArgs): Promise<ActionResult> {
  const { id: orgId, podcastId } = params;

  if (!orgId || !podcastId) {
    return { success: false, error: "Organization ID and Podcast ID are required" };
  }

  const formData = await parseFormData(request);

  // Use _section to scope checkbox coercion to the form that was actually submitted.
  // Without this, submitting one form section silently resets booleans from other sections.
  const section = formData._section;
  const { _section, ...fields } = formData;

  const dataWithDefaults: Record<string, unknown> = { ...fields };

  // Only coerce explicit when submitting the general section
  if (!section || section === "general") {
    dataWithDefaults.explicit = fields.explicit === "true" || fields.explicit === "on";
  }

  // Only coerce isPremium when submitting the monetization section
  if (section === "monetization") {
    dataWithDefaults.isPremium = fields.isPremium === "true" || fields.isPremium === "on";
  }

  const result = updatePodcastSchema.safeParse(dataWithDefaults);

  if (!result.success) {
    return { success: false, fieldErrors: extractFieldErrors(result.error) };
  }

  try {
    const podcast = await podcastAPI.update(orgId, podcastId, result.data);
    return { success: true, data: podcast };
  } catch (error) {
    if (error instanceof APIError) {
      return { success: false, error: error.message };
    }
    return { success: false, error: "An unexpected error occurred" };
  }
}

export async function deletePodcastAction({
  params,
}: ActionFunctionArgs): Promise<ActionResult | Response> {
  const { id: orgId, podcastId } = params;

  if (!orgId || !podcastId) {
    return { success: false, error: "Organization ID and Podcast ID are required" };
  }

  try {
    await podcastAPI.delete(orgId, podcastId);
    return redirect(`/organizations/${orgId}/podcasts`);
  } catch (error) {
    if (error instanceof APIError) {
      return { success: false, error: error.message };
    }
    return { success: false, error: "An unexpected error occurred" };
  }
}
